// rosterApi — typed CRUD for /v1/schedules/roster (Phase 1A of the
// schedule build-out). The "shifts" table holds recurring templates;
// this is the layer that says "Officer X is on Shift Y at Site Z on
// 2026-05-12". The dashboard month-calendar reads from here.
import { apiFetch } from "../../lib/api-client.js";

export type RosterStatus = "scheduled" | "confirmed" | "swapped" | "cancelled";

export interface RosterEntry {
  id: string;
  tenant_id: string;
  site_id: string;
  shift_id: string;
  officer_id: string;
  work_date: string;       // YYYY-MM-DD
  status: RosterStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields (server-side):
  shift_name: string | null;
  shift_start: string | null;
  shift_end: string | null;
  shift_officer_count: number | null;
  officer_name: string | null;
  site_name: string | null;
}

export interface RosterListResponse { items: RosterEntry[]; correlation_id: string | null }

export class RosterApiError extends Error {
  status: number; code: string; correlationId: string | null; fields?: string[]; upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, fields?: string[], upstreamMessage?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.fields = fields; this.upstreamMessage = upstreamMessage;
  }
}

export function friendlyRosterError(err: unknown): string {
  if (err instanceof RosterApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能修改排班。";
    if (err.status === 404) return "找不到此排班記錄。";
    if (err.status === 409 && err.code === "duplicate_assignment")
      return "這名員工已被排在同一個班別、同一天，不能重複排班。";
    if (err.status === 409 && err.code === "overlap_with_other_shift")
      return err.upstreamMessage ?? "這名員工這天已經有時間重疊的班別。";
    if (err.status === 400 && err.code === "tenant_id_required") return "尚未選定操作的單位。";
    if (err.status === 400 && err.code === "officer_not_found") return "找不到指定的員工。";
    if (err.status === 400 && err.code === "shift_not_found") return "找不到指定的班別。";
    if (err.status === 400 && err.code === "site_not_found") return "找不到指定的案場。";
    if (err.status === 400 && err.code === "invalid_body") {
      return `欄位填寫不完整：${(err.fields ?? []).join("、")}。`;
    }
    if (err.status === 400) return "資料格式不正確。";
    const msgPart = err.upstreamMessage ? `・msg=${err.upstreamMessage}` : "";
    return `排班資料暫時無法存取（HTTP ${err.status}・code=${err.code}${msgPart}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<RosterApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let fields: string[] | undefined; let msg: string | undefined;
  try { const b = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[]; message?: string }; code = b.error ?? code; cid = b.correlation_id ?? cid; fields = b.fields; msg = b.message; } catch {}
  return new RosterApiError(r.status, code, cid, fields, msg);
}

export interface RosterQuery {
  tenantId?: string | null;
  /** YYYY-MM-DD inclusive lower bound. */
  from?: string | null;
  /** YYYY-MM-DD inclusive upper bound. */
  to?: string | null;
  siteId?: string | null;
  officerId?: string | null;
  shiftId?: string | null;
}

export async function listRoster(params: RosterQuery = {}): Promise<RosterListResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.siteId) qs.set("site_id", params.siteId);
  if (params.officerId) qs.set("officer_id", params.officerId);
  if (params.shiftId) qs.set("shift_id", params.shiftId);
  const r = await apiFetch(`/v1/schedules/roster${qs.toString() ? `?${qs}` : ""}`);
  if (!r.ok) throw await asError(r);
  return (await r.json()) as RosterListResponse;
}

export interface CreateRosterBody {
  shift_id: string;
  officer_id: string;
  site_id: string;
  work_date: string;       // YYYY-MM-DD
  notes?: string | null;
  status?: RosterStatus;
}

export async function createRoster(body: CreateRosterBody, tenantId?: string | null): Promise<{ id: string }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/roster${qs}`, {
    method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export type PatchRosterBody = Partial<{ status: RosterStatus; officer_id: string; notes: string | null }>;

export async function patchRoster(id: string, body: PatchRosterBody, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/roster/${encodeURIComponent(id)}${qs}`, {
    method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
}

export async function cancelRoster(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/roster/${encodeURIComponent(id)}${qs}`, { method: "DELETE" });
  if (!r.ok) throw await asError(r);
}

export interface OfficerPickerRow { id: string; display_name: string; status: string }
export async function listScheduleOfficers(tenantId?: string | null): Promise<OfficerPickerRow[]> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/officers${qs}`);
  if (!r.ok) throw await asError(r);
  const body = (await r.json()) as { items: OfficerPickerRow[] };
  return body.items ?? [];
}

export interface MonthlyHoursRow {
  officer_id: string;
  officer_name: string | null;
  total_minutes: number;
  total_hours: number;
  shifts_count: number;
  days_worked: number;
}
export interface MonthlyHoursResponse {
  items: MonthlyHoursRow[];
  totals: { officers: number; total_minutes: number; total_hours: number; total_shifts: number };
  month: string;
  from: string;
  to: string;
}
export async function fetchMonthlyHours(month: string, tenantId?: string | null): Promise<MonthlyHoursResponse> {
  const qs = new URLSearchParams();
  if (tenantId) qs.set("tenant_id", tenantId);
  if (month) qs.set("month", month);
  const r = await apiFetch(`/v1/schedules/hours${qs.toString() ? `?${qs}` : ""}`);
  if (!r.ok) throw await asError(r);
  return (await r.json()) as MonthlyHoursResponse;
}

export interface GeneratedRosterProposal {
  shift_id: string; shift_name: string;
  officer_id: string; officer_name: string;
  site_id: string; site_name: string;
  work_date: string;
}
export interface GeneratedRosterSkip {
  shift_id: string; shift_name: string; site_id: string; work_date: string;
  reason: "no_eligible_officer" | "skipped_by_exception" | "already_filled";
  needed: number; filled: number;
}
export interface GenerateRosterResponse {
  ok: true;
  dry_run: boolean;
  proposed: GeneratedRosterProposal[];
  skipped: GeneratedRosterSkip[];
  created_ids?: string[];
  summary: { proposed?: number; created?: number; skipped: number; officers_used: number };
}

export interface GenerateRosterBody {
  from: string;
  to: string;
  site_id?: string | null;
  shift_ids?: string[];
  dry_run?: boolean;
  overwrite_existing?: boolean;
}
export async function generateRoster(body: GenerateRosterBody, tenantId?: string | null): Promise<GenerateRosterResponse> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/roster/generate${qs}`, {
    method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as GenerateRosterResponse;
}
